import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Chip, Link, Paper, Stack, TextField, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { formatLocalDate, formatLocalTime, formatLocalWeekday } from '../../utils/scheduleFormat';

export const TeamName = ({ teamId, name, teamsById }) => {
  const slug = teamsById?.get(teamId)?.slug;
  if (!slug) return <>{name}</>;
  return (
    <Link component={RouterLink} to={`/teams/${slug}`} underline="hover" color="inherit">
      {name}
    </Link>
  );
};

const describeRounds = (rounds = []) => {
  if (rounds.length === 0) return '';
  const sorted = [...rounds].sort((a, b) => a - b);
  return sorted.length === 1 ? `Round ${sorted[0]}` : `Rounds ${sorted.join(', ')}`;
};

const describeVenue = (game) => [game.venueName, game.fieldName].filter(Boolean).join(' / ');

const ScheduleWeeksView = ({ result, teams = [] }) => {
  const theme = useTheme();
  const [highlightTeamId, setHighlightTeamId] = useState('');

  const teamsById = useMemo(() => new Map(teams.map((t) => [t.id, t])), [teams]);
  const teamOptions = useMemo(
    () => (result?.teamStats || []).map((t) => ({ id: t.teamId, name: t.teamName })),
    [result],
  );
  const highlightId = highlightTeamId === '' ? null : Number(highlightTeamId);
  const weeks = result?.weeks || [];

  if (weeks.length === 0) {
    return <Typography color="text.secondary">No weeks were generated.</Typography>;
  }

  const involves = (game) => highlightId !== null && (game.homeTeamId === highlightId || game.awayTeamId === highlightId);

  return (
    <Stack spacing={2}>
      <TextField
        label="Highlight team"
        value={highlightTeamId}
        onChange={(e) => setHighlightTeamId(e.target.value)}
        sx={{ width: { xs: '100%', sm: 280 } }}
        slotProps={{ select: { native: true }, inputLabel: { shrink: true } }}
        select
      >
        <option value="">All teams</option>
        {teamOptions.map((t) => (
          <option key={t.id} value={t.id}>{t.name}</option>
        ))}
      </TextField>

      {weeks.map((week) => {
        const dates = week.gameDates?.length ? week.gameDates : [week.weekStartDate];
        const multipleDates = dates.length > 1;
        const cycles = [...new Set((week.games || []).map((g) => g.cycleNumber).filter(Boolean))];
        const cycleLabel = cycles.length && Math.max(...cycles) > 1 ? `Cycle ${cycles.join(', ')}` : '';
        const key = `${week.weekStartDate}-${week.weekNumber ?? 'blackout'}`;

        if (week.isBlackout) {
          return (
            <Paper
              key={key}
              variant="outlined"
              data-testid="blackout-week"
              sx={{ p: 2, bgcolor: 'grey.100', color: 'text.secondary' }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {formatLocalDate(dates[0])}
              </Typography>
              <Typography variant="body2">
                No games{week.blackoutLabel ? ` – ${week.blackoutLabel}` : ''}
              </Typography>
            </Paper>
          );
        }

        const meta = [describeRounds(week.roundNumbers), cycleLabel].filter(Boolean).join(' · ');

        return (
          <Paper key={key} variant="outlined" sx={{ p: { xs: 1.5, sm: 2 } }} data-testid="schedule-week">
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{ mb: 1, justifyContent: 'space-between', alignItems: { xs: 'flex-start', sm: 'center' } }}
            >
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                {`Week ${week.weekNumber} · ${dates.map(formatLocalDate).join(' & ')}`}
              </Typography>
              {meta && <Typography variant="caption" color="text.secondary">{meta}</Typography>}
            </Stack>
            <Stack spacing={0.5}>
              {(week.games || []).map((game, index) => {
                const highlighted = involves(game);
                const timeLabel = `${multipleDates ? `${formatLocalWeekday(game.date)} ` : ''}${formatLocalTime(game.startTime)}`;
                return (
                  <Box
                    key={`${game.date}-${game.startTime}-${game.homeTeamId}-${index}`}
                    data-testid={highlighted ? 'highlighted-game' : 'schedule-game'}
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { xs: 0, sm: 2 },
                      px: 1,
                      py: 0.75,
                      borderRadius: 1,
                      bgcolor: highlighted ? alpha(theme.palette.primary.main, 0.12) : 'transparent',
                      opacity: highlightId !== null && !highlighted ? 0.55 : 1,
                    }}
                  >
                    {game.isBye ? (
                      <>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: { sm: 110 } }}>
                          {multipleDates ? formatLocalWeekday(game.date) : ''}
                        </Typography>
                        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                          <Chip label="BYE" size="small" clickable={false} onClick={() => {}} />
                          <Typography variant="body2" sx={{ fontWeight: highlighted ? 700 : 400, wordBreak: 'break-word' }}>
                            <TeamName teamId={game.homeTeamId} name={game.homeTeamName} teamsById={teamsById} />
                          </Typography>
                        </Stack>
                      </>
                    ) : (
                      <>
                        <Typography variant="body2" sx={{ minWidth: { sm: 110 }, fontWeight: 600 }}>
                          {timeLabel}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: { sm: 200 }, wordBreak: 'break-word' }}>
                          {describeVenue(game)}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: highlighted ? 700 : 400, wordBreak: 'break-word' }}>
                          <TeamName teamId={game.homeTeamId} name={game.homeTeamName} teamsById={teamsById} />
                          {' vs '}
                          <TeamName teamId={game.awayTeamId} name={game.awayTeamName} teamsById={teamsById} />
                        </Typography>
                      </>
                    )}
                  </Box>
                );
              })}
            </Stack>
          </Paper>
        );
      })}
    </Stack>
  );
};

export default ScheduleWeeksView;
